"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import WaveDropsScrollingOverlay from "@/components/waves/drops/WaveDropsScrollingOverlay";
import { UnreadDividerProvider, useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { Drop, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { isWaveDirectMessage, toApiWaveMin } from "@/helpers/waves/wave.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useScrollBehavior } from "@/hooks/useScrollBehavior";
import { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import { useWaveBoostedDrops } from "@/hooks/useWaveBoostedDrops";
import { useWaveIsTyping } from "@/hooks/useWaveIsTyping";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { TweetPreviewModeProvider, type TweetPreviewMode } from "@/components/tweets/TweetPreviewModeContext";
import { useWaveDropsClipboard } from "./hooks/useWaveDropsClipboard";
import { useDeferredNewestDrops } from "./hooks/useDeferredNewestDrops";
import { useWaveDropsNotificationRead } from "./hooks/useWaveDropsNotificationRead";
import { useWaveDropsSerialScroll } from "./hooks/useWaveDropsSerialScroll";
import { WaveDropsContent } from "./subcomponents/WaveDropsContent";
const EMPTY_DROPS: Drop[] = [];
interface WaveDropsAllProps {
  readonly waveId: string;
  readonly wave?: ApiWave | undefined;
  readonly dropId: string | null;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly initialDrop: number | null;
  readonly dividerSerialNo?: number | null | undefined;
  readonly unreadCount?: number | undefined;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly bottomPaddingClassName?: string | undefined;
  readonly isMuted?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
}
const WaveDropsAllInner: React.FC<WaveDropsAllProps> = t0 => {
  const $ = _c(95);
  const {
    waveId,
    wave,
    dropId,
    onReply,
    activeDrop,
    initialDrop,
    dividerSerialNo,
    unreadCount,
    onDropContentClick,
    bottomPaddingClassName,
    isMuted: t1,
    winningThreshold,
    isVotingClosed: t2,
    isVotingControlsLocked: t3
  } = t0;
  const isMuted = t1 === undefined ? false : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const router = useRouter();
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  const {
    connectedProfile
  } = useAuth();
  const {
    isAppleMobile
  } = useDeviceInfo();
  const containerRef = useRef(null);
  const {
    waveMessages,
    fetchNextPage,
    waitAndRevealDrop
  } = useVirtualizedWaveDrops(waveId, dropId, wave);
  const {
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  const typingMessage = useWaveIsTyping(waveId, connectedProfile?.handle ?? null, isMuted);
  let t4;
  if ($[0] !== wave || $[1] !== waveId) {
    t4 = {
      waveId,
      wave
    };
    $[0] = wave;
    $[1] = waveId;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    data: boostedDrops
  } = useWaveBoostedDrops(t4);
  const scrollBehavior = useScrollBehavior();
  const {
    scrollContainerRef,
    scrollContainerCallbackRef,
    bottomAnchorRef,
    bottomAnchorCallbackRef,
    isAtBottom,
    shouldPinToBottom,
    scrollToVisualBottom,
    forcePinToBottom
  } = scrollBehavior;
  const t5 = Boolean(connectedProfile?.handle);
  let t6;
  if ($[3] !== removeWaveDeliveredNotifications || $[4] !== t5 || $[5] !== waveId) {
    t6 = {
      waveId,
      enabled: t5,
      removeWaveDeliveredNotifications
    };
    $[3] = removeWaveDeliveredNotifications;
    $[4] = t5;
    $[5] = waveId;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  useWaveDropsNotificationRead(t6);
  waveMessages?.drops;
  const dropsForClipboard = waveMessages?.drops ?? EMPTY_DROPS;
  let t7;
  if ($[7] !== dropsForClipboard) {
    t7 = {
      containerRef,
      drops: dropsForClipboard
    };
    $[7] = dropsForClipboard;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  useWaveDropsClipboard(t7);
  const initializedWaveRef = useRef(null);
  let t8;
  let t9;
  if ($[9] !== dividerSerialNo || $[10] !== setUnreadDividerSerialNo || $[11] !== waveId) {
    t8 = () => {
      if (initializedWaveRef.current === waveId) {
        return;
      }
      initializedWaveRef.current = waveId;
      setUnreadDividerSerialNo(dividerSerialNo ?? null);
    };
    t9 = [waveId, dividerSerialNo, setUnreadDividerSerialNo];
    $[9] = dividerSerialNo;
    $[10] = setUnreadDividerSerialNo;
    $[11] = waveId;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  if ($[14] !== isAppleMobile || $[15] !== shouldPinToBottom || $[16] !== waveId || $[17] !== waveMessages) {
    t10 = {
      waveId,
      isAppleMobile,
      waveMessages,
      shouldPinToBottom
    };
    $[14] = isAppleMobile;
    $[15] = shouldPinToBottom;
    $[16] = waveId;
    $[17] = waveMessages;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const {
    renderedWaveMessages,
    pendingDropsCount,
    revealPendingDrops: revealDeferredPendingDrops
  } = useDeferredNewestDrops(t10);
  let t11;
  bb0: {
    if (!renderedWaveMessages || !wave) {
      t11 = renderedWaveMessages;
      break bb0;
    }
    let t12;
    let t13;
    if ($[19] !== renderedWaveMessages || $[20] !== wave) {
      const waveMin = toApiWaveMin(wave);
      t12 = renderedWaveMessages;
      t13 = renderedWaveMessages.drops.map(drop => drop.type === DropSize.FULL && drop.wave.id === wave.id ? {
        ...drop,
        wave: waveMin
      } : drop);
      $[19] = renderedWaveMessages;
      $[20] = wave;
      $[21] = t12;
      $[22] = t13;
    } else {
      t12 = $[21];
      t13 = $[22];
    }
    let t14;
    if ($[23] !== t12 || $[24] !== t13) {
      t14 = {
        ...t12,
        drops: t13
      };
      $[23] = t12;
      $[24] = t13;
      $[25] = t14;
    } else {
      t14 = $[25];
    }
    t11 = t14;
  }
  const renderedWaveMessagesWithFullWave = t11;
  let t12;
  if ($[26] !== dropId || $[27] !== fetchNextPage || $[28] !== initialDrop || $[29] !== renderedWaveMessages || $[30] !== scrollContainerRef || $[31] !== scrollToVisualBottom || $[32] !== shouldPinToBottom || $[33] !== waitAndRevealDrop || $[34] !== waveId || $[35] !== waveMessages) {
    t12 = {
      waveId,
      dropId,
      initialDrop,
      waveMessages,
      renderedWaveMessages,
      fetchNextPage,
      waitAndRevealDrop,
      scrollContainerRef,
      shouldPinToBottom,
      scrollToVisualBottom
    };
    $[26] = dropId;
    $[27] = fetchNextPage;
    $[28] = initialDrop;
    $[29] = renderedWaveMessages;
    $[30] = scrollContainerRef;
    $[31] = scrollToVisualBottom;
    $[32] = shouldPinToBottom;
    $[33] = waitAndRevealDrop;
    $[34] = waveId;
    $[35] = waveMessages;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const {
    serialTarget,
    queueSerialTarget,
    targetDropRef,
    isScrolling,
    scrollBaselineSerials,
    frozenAutoCollapseSerials
  } = useWaveDropsSerialScroll(t12);
  let t13;
  if ($[37] !== frozenAutoCollapseSerials || $[38] !== isScrolling || $[39] !== renderedWaveMessages?.drops || $[40] !== scrollBaselineSerials) {
    t13 = () => {
      if (!isScrolling || !scrollBaselineSerials) {
        return frozenAutoCollapseSerials;
      }
      const drops = renderedWaveMessages?.drops;
      if (!drops || drops.length === 0) {
        return frozenAutoCollapseSerials;
      }
      const next = new Set(frozenAutoCollapseSerials);
      for (const drop_0 of drops) {
        const serialNo = drop_0.serial_no;
        if (typeof serialNo !== "number") {
          continue;
        }
        if (!scrollBaselineSerials.has(serialNo) && !next.has(serialNo)) {
          next.add(serialNo);
        }
      }
      return next;
    };
    $[37] = frozenAutoCollapseSerials;
    $[38] = isScrolling;
    $[39] = renderedWaveMessages?.drops;
    $[40] = scrollBaselineSerials;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  renderedWaveMessages?.drops;
  let t14;
  if ($[42] !== t13) {
    t14 = t13();
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const autoCollapseSerials = t14;
  const waveChatScroll = useWaveChatScrollOptional();
  let t15;
  let t16;
  if ($[44] !== queueSerialTarget || $[45] !== waveChatScroll || $[46] !== waveId) {
    t15 = () => {
      if (!waveChatScroll) {
        return;
      }
      return waveChatScroll.registerScrollHandler({
        waveId,
        handler: queueSerialTarget
      });
    };
    t16 = [waveChatScroll, waveId, queueSerialTarget];
    $[44] = queueSerialTarget;
    $[45] = waveChatScroll;
    $[46] = waveId;
    $[47] = t15;
    $[48] = t16;
  } else {
    t15 = $[47];
    t16 = $[48];
  }
  useEffect(t15, t16);
  let t17;
  if ($[49] !== forcePinToBottom || $[50] !== revealDeferredPendingDrops || $[51] !== waveMessages?.drops.length) {
    t17 = () => {
      if ((waveMessages?.drops.length ?? 0) === 0) {
        return;
      }
      revealDeferredPendingDrops();
      forcePinToBottom();
    };
    $[49] = forcePinToBottom;
    $[50] = revealDeferredPendingDrops;
    $[51] = waveMessages?.drops.length;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  const revealPendingDrops = t17;
  const canFetchMoreDrops = !!waveMessages && waveMessages.hasNextPage && !waveMessages.isLoading && !waveMessages.isLoadingNextPage;
  let t18;
  if ($[53] !== canFetchMoreDrops || $[54] !== dropId || $[55] !== fetchNextPage || $[56] !== waveId) {
    t18 = () => {
      if (!canFetchMoreDrops) {
        return;
      }
      fetchNextPage({
        waveId,
        type: DropSize.FULL
      }, dropId).catch(_temp);
    };
    $[53] = canFetchMoreDrops;
    $[54] = dropId;
    $[55] = fetchNextPage;
    $[56] = waveId;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  const handleTopIntersection = t18;
  let t19;
  if ($[58] !== queueSerialTarget || $[59] !== router || $[60] !== waveId) {
    t19 = drop_1 => {
      if (drop_1.wave.id === waveId) {
        queueSerialTarget(drop_1.serial_no);
      } else {
        const waveDetails = drop_1.wave as unknown as {
          chat?: {
            scope?: {
              group?: {
                is_direct_message?: boolean | undefined;
              } | undefined;
            } | undefined;
          } | undefined;
        };
        const isDirectMessage = isWaveDirectMessage(drop_1.wave.id, waveDetails);
        const href = getWaveRoute({
          waveId: drop_1.wave.id,
          serialNo: drop_1.serial_no,
          isDirectMessage,
          isApp: false
        });
        router.push(href);
      }
    };
    $[58] = queueSerialTarget;
    $[59] = router;
    $[60] = waveId;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  const handleQuoteClick = t19;
  const t20 = isScrolling || serialTarget !== null;
  let t21;
  if ($[62] !== activeDrop || $[63] !== autoCollapseSerials || $[64] !== boostedDrops || $[65] !== bottomAnchorCallbackRef || $[66] !== bottomAnchorRef || $[67] !== bottomPaddingClassName || $[68] !== dropId || $[69] !== forcePinToBottom || $[70] !== handleQuoteClick || $[71] !== handleTopIntersection || $[72] !== isAtBottom || $[73] !== isVotingClosed || $[74] !== isVotingControlsLocked || $[75] !== onDropContentClick || $[76] !== onReply || $[77] !== pendingDropsCount || $[78] !== queueSerialTarget || $[79] !== renderedWaveMessagesWithFullWave || $[80] !== revealPendingDrops || $[81] !== scrollContainerCallbackRef || $[82] !== scrollContainerRef || $[83] !== serialTarget || $[84] !== t20 || $[85] !== targetDropRef || $[86] !== typingMessage || $[87] !== unreadCount || $[88] !== winningThreshold) {
    t21 = <TweetPreviewModeProvider mode="never"><WaveDropsContent waveMessages={renderedWaveMessagesWithFullWave} dropId={dropId} scrollContainerRef={scrollContainerRef} scrollContainerCallbackRef={scrollContainerCallbackRef} bottomAnchorRef={bottomAnchorRef} bottomAnchorCallbackRef={bottomAnchorCallbackRef} onTopIntersection={handleTopIntersection} onReply={onReply} queueSerialTarget={queueSerialTarget} activeDrop={activeDrop} serialTarget={serialTarget} targetDropRef={targetDropRef} onQuoteClick={handleQuoteClick} isAtBottom={isAtBottom} scrollToBottom={forcePinToBottom} typingMessage={typingMessage} onDropContentClick={onDropContentClick} pendingCount={pendingDropsCount} onRevealPending={revealPendingDrops} bottomPaddingClassName={bottomPaddingClassName} boostedDrops={boostedDrops} onBoostedDropClick={queueSerialTarget} onScrollToUnread={queueSerialTarget} unreadCount={unreadCount} autoCollapseSerials={autoCollapseSerials} suspendLightDropHydration={t20} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} /></TweetPreviewModeProvider>;
    $[62] = activeDrop;
    $[63] = autoCollapseSerials;
    $[64] = boostedDrops;
    $[65] = bottomAnchorCallbackRef;
    $[66] = bottomAnchorRef;
    $[67] = bottomPaddingClassName;
    $[68] = dropId;
    $[69] = forcePinToBottom;
    $[70] = handleQuoteClick;
    $[71] = handleTopIntersection;
    $[72] = isAtBottom;
    $[73] = isVotingClosed;
    $[74] = isVotingControlsLocked;
    $[75] = onDropContentClick;
    $[76] = onReply;
    $[77] = pendingDropsCount;
    $[78] = queueSerialTarget;
    $[79] = renderedWaveMessagesWithFullWave;
    $[80] = revealPendingDrops;
    $[81] = scrollContainerCallbackRef;
    $[82] = scrollContainerRef;
    $[83] = serialTarget;
    $[84] = t20;
    $[85] = targetDropRef;
    $[86] = typingMessage;
    $[87] = unreadCount;
    $[88] = winningThreshold;
    $[89] = t21;
  } else {
    t21 = $[89];
  }
  let t22;
  if ($[90] !== isScrolling) {
    t22 = <WaveDropsScrollingOverlay isVisible={isScrolling} />;
    $[90] = isScrolling;
    $[91] = t22;
  } else {
    t22 = $[91];
  }
  let t23;
  if ($[92] !== t21 || $[93] !== t22) {
    t23 = <div ref={containerRef} className="tw-relative tw-flex tw-h-full tw-flex-col tw-justify-end tw-overflow-hidden tw-bg-iron-950">{t21}{t22}</div>;
    $[92] = t21;
    $[93] = t22;
    $[94] = t23;
  } else {
    t23 = $[94];
  }
  return t23;
};
export const WaveDropsAllWithoutProvider: React.FC<WaveDropsAllProps> = WaveDropsAllInner;
const WaveDropsAll: React.FC<WaveDropsAllProps> = t0 => {
  const $ = _c(19);
  const {
    waveId,
    wave,
    dropId,
    onReply,
    activeDrop,
    initialDrop,
    dividerSerialNo,
    unreadCount,
    onDropContentClick,
    bottomPaddingClassName,
    isMuted: t1,
    winningThreshold,
    isVotingClosed: t2,
    isVotingControlsLocked: t3
  } = t0;
  const isMuted = t1 === undefined ? false : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const t4 = dividerSerialNo ?? null;
  const t5 = `unread-divider-${waveId}`;
  let t6;
  if ($[0] !== activeDrop || $[1] !== bottomPaddingClassName || $[2] !== dividerSerialNo || $[3] !== dropId || $[4] !== initialDrop || $[5] !== isMuted || $[6] !== isVotingClosed || $[7] !== isVotingControlsLocked || $[8] !== onDropContentClick || $[9] !== onReply || $[10] !== unreadCount || $[11] !== wave || $[12] !== waveId || $[13] !== winningThreshold) {
    t6 = <WaveDropsAllInner key={waveId} waveId={waveId} wave={wave} dropId={dropId} onReply={onReply} activeDrop={activeDrop} initialDrop={initialDrop} dividerSerialNo={dividerSerialNo} unreadCount={unreadCount} onDropContentClick={onDropContentClick} bottomPaddingClassName={bottomPaddingClassName} isMuted={isMuted} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />;
    $[0] = activeDrop;
    $[1] = bottomPaddingClassName;
    $[2] = dividerSerialNo;
    $[3] = dropId;
    $[4] = initialDrop;
    $[5] = isMuted;
    $[6] = isVotingClosed;
    $[7] = isVotingControlsLocked;
    $[8] = onDropContentClick;
    $[9] = onReply;
    $[10] = unreadCount;
    $[11] = wave;
    $[12] = waveId;
    $[13] = winningThreshold;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    t7 = <UnreadDividerProvider initialSerialNo={t4} key={t5}>{t6}</UnreadDividerProvider>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};
export default WaveDropsAll;
function _temp() {}